'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useLanguage } from '@/components/language-provider';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Lock,
  Volume2,
  Mountain,
  ShieldCheck,
  Layers,
  Feather,
  ArrowRight,
  Beaker,
  Factory,
  Handshake
} from 'lucide-react';

export default function ChallengesPage() {
  const { t } = useLanguage();

  const challenges = [
    {
      id: 1,
      icon: Lock,
      titleKey: 'challenges.1.title',
      problemKey: 'challenges.1.pain',
      solutionKey: 'challenges.1.solution',
      bgImage: '/challenge-grip.jpg',
      gradient: 'from-rose-500/10 to-transparent',
      iconColor: 'text-rose-500',
      hoverBorder: 'hover:border-rose-500/50',
      gradientHover: 'group-hover:from-rose-500/15'
    },
    {
      id: 2,
      icon: Volume2,
      titleKey: 'challenges.2.title',
      problemKey: 'challenges.2.pain',
      solutionKey: 'challenges.2.solution',
      bgImage: '/challenge-buffer.jpg',
      gradient: 'from-orange-500/10 to-transparent',
      iconColor: 'text-orange-500',
      hoverBorder: 'hover:border-orange-500/50',
      gradientHover: 'group-hover:from-orange-500/15'
    },
    {
      id: 3,
      icon: Mountain,
      titleKey: 'challenges.3.title',
      problemKey: 'challenges.3.pain',
      solutionKey: 'challenges.3.solution',
      bgImage: '/challenge-terrain.jpg',
      gradient: 'from-sky-500/10 to-transparent',
      iconColor: 'text-sky-500',
      hoverBorder: 'hover:border-sky-500/50',
      gradientHover: 'group-hover:from-sky-500/15'
    },
    {
      id: 4,
      icon: ShieldCheck,
      titleKey: 'challenges.4.title',
      problemKey: 'challenges.4.pain',
      solutionKey: 'challenges.4.solution',
      bgImage: '/challenge-wear.jpg',
      gradient: 'from-emerald-500/10 to-transparent',
      iconColor: 'text-emerald-500',
      hoverBorder: 'hover:border-emerald-500/50',
      gradientHover: 'group-hover:from-emerald-500/15'
    },
    {
      id: 5,
      icon: Layers,
      titleKey: 'challenges.5.title',
      problemKey: 'challenges.5.pain',
      solutionKey: 'challenges.5.solution',
      bgImage: '/challenge-standard.jpg',
      gradient: 'from-violet-500/10 to-transparent',
      iconColor: 'text-violet-500',
      hoverBorder: 'hover:border-violet-500/50',
      gradientHover: 'group-hover:from-violet-500/15'
    },
    {
      id: 6,
      icon: Feather,
      titleKey: 'challenges.6.title',
      problemKey: 'challenges.6.pain',
      solutionKey: 'challenges.6.solution',
      bgImage: '/challenge-lightweight.jpg',
      gradient: 'from-indigo-500/10 to-transparent',
      iconColor: 'text-indigo-500',
      hoverBorder: 'hover:border-indigo-500/50',
      gradientHover: 'group-hover:from-indigo-500/15'
    }
  ];

  const solutions = [
    {
      id: 'custom',
      image: '/solution-custom.jpg',
      icon: Beaker,
      titleKey: 'solutions.custom.title',
      subtitleKey: 'solutions.custom.subtitle',
      descKey: 'solutions.custom.desc',
      details: [
        { title: 'solutions.custom.detail1.title', content: 'solutions.custom.detail1.content' },
        { title: 'solutions.custom.detail2.title', content: 'solutions.custom.detail2.content' },
        { title: 'solutions.custom.detail3.title', content: 'solutions.custom.detail3.content' }
      ]
    },
    {
      id: 'oem',
      image: '/solution-oem.jpg',
      icon: Factory,
      titleKey: 'solutions.oem.title',
      subtitleKey: 'solutions.oem.subtitle',
      descKey: 'solutions.oem.desc',
      details: [
        { title: 'solutions.oem.detail1.title', content: 'solutions.oem.detail1.content' },
        { title: 'solutions.oem.detail2.title', content: 'solutions.oem.detail2.content' },
        { title: 'solutions.oem.detail3.title', content: 'solutions.oem.detail3.content' }
      ]
    },
    {
      id: 'collab',
      image: '/solution-collab.jpg',
      icon: Handshake,
      titleKey: 'solutions.collab.title',
      subtitleKey: 'solutions.collab.subtitle',
      descKey: 'solutions.collab.desc',
      details: [
        { title: 'solutions.collab.detail1.title', content: 'solutions.collab.detail1.content' },
        { title: 'solutions.collab.detail2.title', content: 'solutions.collab.detail2.content' },
        { title: 'solutions.collab.detail3.title', content: 'solutions.collab.detail3.content' }
      ]
    }
  ];

  const processSteps = [
    { step: '01', titleKey: 'solutions.process.step1' },
    { step: '02', titleKey: 'solutions.process.step2' },
    { step: '03', titleKey: 'solutions.process.step3' },
    { step: '04', titleKey: 'solutions.process.step4' },
    { step: '05', titleKey: 'solutions.process.step5' },
    { step: '06', titleKey: 'solutions.process.step6' }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <Image
            src="/challenges-hero.png"
            alt="RobotSole"
            fill
            priority
            className="object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/85 to-background" />
        </div>
        <div className="relative z-10 container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Badge variant="outline" className="mb-6 px-4 py-1.5 border-primary/30 text-primary bg-background/50 backdrop-blur-sm">
              {t('challenges.badge')}
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-foreground">
              {t('challenges.title')}
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              {t('challenges.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-8 md:py-12">
        <div className="container mx-auto px-4 md:px-6">
          <p className="text-center text-muted-foreground text-lg max-w-3xl mx-auto">
            {t('challenges.intro')}
          </p>
        </div>
      </section>

      {/* Challenges Grid */}
      <section className="pb-16 md:pb-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {challenges.map((challenge) => {
              const Icon = challenge.icon;
              return (
                <Card
                  key={challenge.id}
                  className={`group relative overflow-hidden rounded-xl border border-border ${challenge.hoverBorder} transition-colors min-h-[200px]`}
                >
                  <div className="absolute inset-0">
                    <Image
                      src={challenge.bgImage}
                      alt=""
                      fill
                      sizes="(max-width: 768px) 100vw, 50vw"
                      className="object-cover opacity-30"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-br ${challenge.gradient} ${challenge.gradientHover} transition-all duration-300`} />
                    <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-10" />
                  </div>

                  <div className="relative z-10 p-6 flex flex-col h-full">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="p-2 rounded-lg bg-primary/30 backdrop-blur-sm">
                        <Icon className={`h-5 w-5 ${challenge.iconColor}`} />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">
                        {t(challenge.titleKey)}
                      </h3>
                    </div>

                    <div className="space-y-3 flex-1">
                      <p className="text-sm text-foreground leading-relaxed">
                        {t(challenge.problemKey)}
                      </p>
                      <div className="border-t border-border/60 pt-3 flex items-start gap-2">
                        <p className="text-sm text-foreground leading-relaxed">
                          {t(challenge.solutionKey)}
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Outro */}
      <section className="pb-16 md:pb-20">
        <div className="container mx-auto px-4 md:px-6">
          <p className="text-center text-foreground text-lg max-w-3xl mx-auto font-medium">
            {t('challenges.outro')}
          </p>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4 px-4 py-1.5">
              {t('solutions.title')}
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('solutions.title')}</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{t('solutions.subtitle')}</p>
          </div>

          <p className="text-center text-muted-foreground text-lg max-w-3xl mx-auto mb-16">
            {t('solutions.intro')}
          </p>

          <div className="grid gap-12 lg:gap-20">
            {solutions.map((solution, index) => {
              const Icon = solution.icon;
              const isReversed = index % 2 === 1;

              return (
                <div
                  key={solution.id}
                  className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center"
                >
                  {/* Image */}
                  <div className={`relative aspect-[4/3] rounded-2xl overflow-hidden ${isReversed ? 'lg:order-2' : ''}`}>
                    <Image
                      src={solution.image}
                      alt={t(solution.titleKey)}
                      fill
                      className="object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                  </div>

                  {/* Content */}
                  <div className={isReversed ? 'lg:order-1' : ''}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                        <Icon className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
                      </div>
                      <h3 className="text-2xl md:text-3xl font-bold">
                        {t(solution.titleKey)}
                      </h3>
                    </div>

                    <p className="text-cyan-600 dark:text-cyan-400 font-medium mb-4">
                      {t(solution.subtitleKey)}
                    </p>

                    <p className="text-muted-foreground text-lg mb-6">
                      {t(solution.descKey)}
                    </p>

                    <div className="space-y-4 mb-8">
                      {solution.details.map((detail, i) => (
                        <div key={i} className="p-4 rounded-lg bg-background border border-border/50">
                          <h4 className="font-semibold text-foreground mb-2">{t(detail.title)}</h4>
                          <p className="text-muted-foreground text-sm">{t(detail.content)}</p>
                        </div>
                      ))}
                    </div>

                    <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" asChild>
                      <Link href="/contact">
                        {t('solutions.cta.button')}
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Link>
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <Badge variant="outline" className="mb-4 px-4 py-1.5">
                {t('solutions.process.title')}
              </Badge>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {processSteps.map((step, index) => (
                <Card key={index} className="relative h-full border-border/50 hover:border-primary/30 transition-colors group">
                  <CardHeader>
                    <div className="text-5xl font-bold text-primary/20 mb-2 group-hover:text-primary/30 transition-colors">
                      {step.step}
                    </div>
                    <CardTitle className="text-xl">{t(step.titleKey)}</CardTitle>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
              <div className="pt-8 pb-8 px-6 md:px-12">
                <h2 className="text-2xl md:text-3xl font-bold mb-4">
                  {t('solutions.cta.title')}
                </h2>
                <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
                  {t('solutions.cta.subtitle')}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground" asChild>
                    <Link href="/contact">
                      {t('solutions.cta.button')}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </Button>
                  <Button size="lg" variant="outline" asChild>
                    <Link href="/products">
                      {t('products.title')}
                    </Link>
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
